import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { isoToMelbourneLocal, melbourneLocalToISO } from "@/lib/time";
import { Check, X } from "lucide-react";

export default function AdminCorrections() {
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [resolve, setResolve] = useState(null);
  const [form, setForm] = useState({ action: "accept", field: "clock_out", new_time: "", reason: "" });

  const load = useCallback(async () => {
    const params = filter === "all" ? {} : { status: filter };
    const { data } = await api.get("/admin/corrections", { params });
    setItems(data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const openResolve = (c) => {
    setResolve(c);
    setForm({ action: "accept", field: "clock_out", new_time: "", reason: "" });
  };

  const submit = async () => {
    try {
      const payload = {
        action: form.action,
        reason: form.reason,
      };
      if (form.action !== "reject") {
        payload.field = form.field;
        payload.new_time_iso = form.new_time ? melbourneLocalToISO(form.new_time) : null;
      }
      await api.post(`/admin/corrections/${resolve.id}/resolve`, payload);
      toast.success("Correction resolved.");
      setResolve(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const statusColor = { pending: "bg-[hsl(33,70%,90%)] text-[hsl(33,65%,34%)]", accepted: "bg-[hsl(150,40%,90%)] text-[hsl(150,45%,26%)]", rejected: "bg-[hsl(8,60%,92%)] text-[hsl(8,54%,38%)]" };

  return (
    <div className="space-y-6" data-testid="admin-corrections">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-bold">Correction Requests</h1>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="filter-pending">Pending</TabsTrigger>
            <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="space-y-3" data-testid="corrections-list">
        {items.length === 0 && <p className="text-muted-foreground text-sm">No correction requests.</p>}
        {items.map((c) => (
          <Card key={c.id} className="p-5 rounded-xl border-border">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-heading font-semibold">{c.staff_name}</span>
                  <Badge className={statusColor[c.status]}>{c.status}</Badge>
                </div>
                <p className="text-sm"><span className="text-muted-foreground">Date:</span> {c.date}</p>
                <p className="text-sm"><span className="text-muted-foreground">Incorrect:</span> {c.what_incorrect}</p>
                <p className="text-sm"><span className="text-muted-foreground">Should be:</span> {c.correct_time}</p>
                <p className="text-sm"><span className="text-muted-foreground">Explanation:</span> {c.explanation}</p>
                {c.status !== "pending" && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Resolved by {c.resolved_by}
                    {c.original_time && ` • ${c.original_time} → ${c.corrected_time}`}
                    {c.admin_reason && ` • ${c.admin_reason}`}
                  </p>
                )}
              </div>
              {c.status === "pending" && (
                <Button onClick={() => openResolve(c)} data-testid={`resolve-${c.id}`}>Review</Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!resolve} onOpenChange={(o) => !o && setResolve(null)}>
        <DialogContent data-testid="resolve-dialog">
          <DialogHeader><DialogTitle>Review correction — {resolve?.staff_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-sm bg-muted p-3 rounded-lg">
              <p><b>Date:</b> {resolve?.date}</p>
              <p><b>What:</b> {resolve?.what_incorrect}</p>
              <p><b>Requested:</b> {resolve?.correct_time}</p>
            </div>
            <div className="space-y-2">
              <Label>Decision</Label>
              <Select value={form.action} onValueChange={(v) => setForm({ ...form, action: v })}>
                <SelectTrigger data-testid="resolve-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="accept">Accept &amp; apply correction</SelectItem>
                  <SelectItem value="custom">Enter a different correction</SelectItem>
                  <SelectItem value="reject">Reject</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.action !== "reject" && (
              <>
                {!resolve?.shift_id && (
                  <p className="text-xs text-[hsl(8,54%,42%)]">No shift linked to this request — record the decision and edit the shift manually from the staff detail page.</p>
                )}
                <div className="space-y-2">
                  <Label>Which time?</Label>
                  <Select value={form.field} onValueChange={(v) => setForm({ ...form, field: v })}>
                    <SelectTrigger data-testid="resolve-field"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clock_in">Clock-in</SelectItem>
                      <SelectItem value="clock_out">Clock-out</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Correct time</Label>
                  <Input type="datetime-local" value={form.new_time} onChange={(e) => setForm({ ...form, new_time: e.target.value })} data-testid="resolve-new-time" />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Reason (required)</Label>
              <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="resolve-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={!form.reason || (form.action !== "reject" && resolve?.shift_id && !form.new_time)} data-testid="resolve-submit">
              {form.action === "reject" ? <><X className="h-4 w-4 mr-2" /> Reject</> : <><Check className="h-4 w-4 mr-2" /> Confirm</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
